type OrganizationDisplaySource = {
    readonly name: string;
    readonly internal_name?: string | null;
};

type OrganizationMembershipDisplaySource = {
    readonly organization_id: string;
    readonly organization_name: string | null;
    readonly organization_internal_name?: string | null;
};

export type OrganizationMembershipOption =
    OrganizationMembershipDisplaySource & {
        readonly displayName: string;
    };

export function formatOrganizationDisplayName(
    organization: OrganizationDisplaySource,
    userIsAdmin: boolean,
): string {
    const internalName = organization.internal_name?.trim();

    if (userIsAdmin) {
        return internalName
            ? `${organization.name} (${internalName})`
            : organization.name;
    }

    return internalName || organization.name;
}

export function formatOrganizationMembershipDisplayName(
    organization: OrganizationMembershipDisplaySource,
    userIsAdmin: boolean,
): string {
    const internalName = organization.organization_internal_name?.trim();
    const organizationName =
        organization.organization_name?.trim() || 'Unknown organization';

    if (userIsAdmin) {
        return internalName
            ? `${organizationName} (${internalName})`
            : organizationName;
    }

    return internalName || organizationName;
}

export function buildOrganizationMembershipOptions(
    organizations: ReadonlyArray<OrganizationMembershipDisplaySource>,
    userIsAdmin: boolean,
): Array<OrganizationMembershipOption> {
    return [...organizations]
        .map((organization) => ({
            ...organization,
            displayName: formatOrganizationMembershipDisplayName(
                organization,
                userIsAdmin,
            ),
        }))
        .sort((left, right) =>
            left.displayName.localeCompare(right.displayName),
        );
}

export function filterOrganizationMembershipOptions(
    organizations: ReadonlyArray<OrganizationMembershipOption>,
    search: string,
): Array<OrganizationMembershipOption> {
    const searchTerms = search.toLowerCase().split(/\s+/).filter(Boolean);

    if (searchTerms.length === 0) {
        return [...organizations];
    }

    return organizations.filter((organization) => {
        const searchableValues = [
            organization.displayName,
            organization.organization_id,
            organization.organization_name,
            organization.organization_internal_name,
        ];

        return searchTerms.every((term) =>
            searchableValues.some((value) =>
                value?.toLowerCase().includes(term),
            ),
        );
    });
}

export function getOrganizationBadgeColor(
    name: string | null | undefined,
): string | undefined {
    if (!name) {
        return undefined;
    }

    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = (hash << 5) - hash + name.charCodeAt(i);
        hash |= 0;
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue} 50% 60%)`;
}

export function getOrganizationInitial(
    name: string | null | undefined,
): string {
    return name?.trim().charAt(0).toUpperCase() || '?';
}
